import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Truck, PackageX, RefreshCw, Trash2, Send, Check, RotateCcw, Boxes, Calendar, User, Hash, FileText, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  aperto: { label: "Aperto", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  inviato: { label: "Inviato a Magazzino", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  evaso: { label: "Evaso", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};
const Pill = ({ s }) => {
  const st = STATUS_STYLE[s] || STATUS_STYLE.aperto;
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold ${st.cls}`}>{st.label}</span>;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  return day && m && y ? `${day}/${m}/${y}` : s;
};

export default function Trasferimenti() {
  const [odts, setOdts] = useState([]);
  const [missing, setMissing] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/transfer-orders").then((r) => setOdts(r.data)).catch(() => {});
    api.get("/transfer-orders/missing").then((r) => setMissing(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 12000); // polling di sicurezza: le nuove richieste compaiono da sole
    return () => clearInterval(iv);
  }, []);
  useLiveRefresh(load);

  const recompute = async () => {
    try { await api.post("/transfer-orders/recompute"); toast.success("Ordini ricalcolati"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resyncAll = async () => {
    if (!window.confirm("Rilanciare TUTTI gli ODT esistenti verso il Magazzino? Aggiorna i dati (numero ODT, ticket, data) sugli ordini già inviati. Nessun duplicato.")) return;
    try {
      const r = await api.post("/transfer-orders/resync-all");
      const { resynced = 0, total = 0, failed = [] } = r.data || {};
      if (failed.length) toast.warning(`Risincronizzati ${resynced}/${total} ODT · ${failed.length} falliti`);
      else toast.success(`Risincronizzati ${resynced}/${total} ODT verso il Magazzino`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const resend = async (id) => {
    try { await api.post(`/transfer-orders/${id}/send`); toast.success("ODT inviato al Magazzino"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeOdt = async (o) => {
    if (!window.confirm(`Eliminare l'ODT #${o.numero} (ticket ${o.ticket_code})? Verrà rimosso anche dal Magazzino e non sarà rigenerato.`)) return;
    try { await api.delete(`/transfer-orders/${o.id}`); toast.success("ODT eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const handleMissing = async (m) => {
    try { await api.post(`/transfer-orders/missing/${m.id}/handle`); toast.success("Segnato come gestito"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reopenMissing = async (m) => {
    try { await api.post(`/transfer-orders/missing/${m.id}/reopen`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const missingOpen = missing.filter((m) => m.status === "da_ordinare").length;

  const FILTERS = [
    { key: "all", label: "Tutti" },
    { key: "aperto", label: "Aperto" },
    { key: "inviato", label: "Inviato" },
    { key: "evaso", label: "Evaso" },
  ];
  const counts = odts.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});

  // ---- ordinamento + filtri per colonna (stile Excel) ----
  const COLS = [
    { key: "numero", label: "N°", num: true },
    { key: "intervention_date", label: "Intervento" },
    { key: "technician_name", label: "Tecnico" },
    { key: "ticket_code", label: "Ticket" },
    { key: "materials", label: "Materiali" },
    { key: "status", label: "Stato" },
  ];
  const getVal = (o, key) => {
    if (key === "materials") return (o.items || []).map((i) => `${i.name} ${i.sku || ""}`).join(", ");
    if (key === "status") return STATUS_STYLE[o.status]?.label || o.status;
    return o[key] ?? "";
  };
  const [sortKey, setSortKey] = useState("numero");
  const [sortDir, setSortDir] = useState("desc");
  const [colFilters, setColFilters] = useState({});
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const setColFilter = (key, val) => setColFilters((f) => ({ ...f, [key]: val }));

  let viewOdts = statusFilter === "all" ? [...odts] : odts.filter((o) => o.status === statusFilter);
  viewOdts = viewOdts.filter((o) =>
    COLS.every((c) => {
      const q = (colFilters[c.key] || "").trim().toLowerCase();
      return !q || String(getVal(o, c.key)).toLowerCase().includes(q);
    })
  );
  viewOdts.sort((a, b) => {
    const col = COLS.find((c) => c.key === sortKey) || {};
    let av = getVal(a, sortKey), bv = getVal(b, sortKey);
    if (col.num) { av = Number(a.numero) || 0; bv = Number(b.numero) || 0; }
    else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
  const filteredOdts = viewOdts;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-navy">Ordini di trasferimento</h1>
          <p className="text-sm text-muted-foreground">Materiali netti da consegnare ai tecnici per i ticket di oggi e domani (dal Magazzino Centrale) e materiali mancanti da approvvigionare</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resyncAll} data-testid="odt-resync-all"><RefreshCw size={16} /> Risincronizza Magazzino</Button>
          <Button variant="outline" onClick={recompute} data-testid="odt-recompute"><RefreshCw size={16} /> Ricalcola</Button>
        </div>
      </div>

      <Tabs defaultValue="odt">
        <TabsList data-testid="odt-tabs">
          <TabsTrigger value="odt" data-testid="tab-odt">Ordini di trasferimento{odts.length ? ` (${odts.length})` : ""}</TabsTrigger>
          <TabsTrigger value="mancanti" data-testid="tab-mancanti">Materiali mancanti{missingOpen ? ` (${missingOpen})` : ""}</TabsTrigger>
        </TabsList>

        {/* -------------------- ODT -------------------- */}
        <TabsContent value="odt" className="mt-4 space-y-3">
          {odts.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="odt-status-filter">
              {FILTERS.map((f) => {
                const n = f.key === "all" ? odts.length : (counts[f.key] || 0);
                const active = statusFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    data-testid={`odt-filter-${f.key}`}
                    className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${active ? "border-navy bg-navy text-white" : "border-border bg-white text-muted-foreground hover:bg-secondary"}`}
                  >
                    {f.label} <span className="opacity-70">({n})</span>
                  </button>
                );
              })}
            </div>
          )}
          {filteredOdts.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-14 text-center"><Truck className="h-9 w-9 text-muted-foreground/40" /><p className="font-semibold">Nessun ordine di trasferimento</p><p className="text-sm text-muted-foreground">{odts.length === 0 ? "Assegna materiali pianificati ai ticket dei tecnici (oggi/domani) per generarli." : "Nessun ODT con questo stato."}</p></Card>
          ) : (
            <>
            <p className="text-xs text-muted-foreground">Clicca sulle intestazioni per ordinare, usa i campi sotto per filtrare, e clicca una riga per il dettaglio dei materiali inviati al Magazzino.</p>
            <Card className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="odt-table">
                <thead className="bg-secondary/60 text-left text-xs font-bold uppercase text-muted-foreground">
                  <tr>
                    {COLS.map((c) => (
                      <th key={c.key} className={`px-3 py-2 ${c.key === "numero" ? "" : ""}`}>
                        <button
                          type="button"
                          onClick={() => toggleSort(c.key)}
                          className="flex items-center gap-1 uppercase hover:text-navy"
                          data-testid={`odt-sort-${c.key}`}
                        >
                          {c.label}
                          {sortKey === c.key
                            ? (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                            : <ChevronsUpDown size={12} className="opacity-40" />}
                        </button>
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right">Azioni</th>
                  </tr>
                  <tr className="bg-white/60">
                    {COLS.map((c) => (
                      <th key={c.key} className="px-2 pb-2">
                        <input
                          value={colFilters[c.key] || ""}
                          onChange={(e) => setColFilter(c.key, e.target.value)}
                          placeholder="Filtra…"
                          data-testid={`odt-colfilter-${c.key}`}
                          className="w-full rounded border border-border bg-white px-2 py-1 text-xs font-normal normal-case text-foreground placeholder:text-muted-foreground/60 focus:border-navy focus:outline-none"
                        />
                      </th>
                    ))}
                    <th className="px-2 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOdts.map((o) => (
                    <tr key={o.id} onClick={() => setSelected(o)} className="cursor-pointer border-t border-border/60 align-top transition-colors hover:bg-secondary/40" data-testid={`odt-row-${o.numero}`}>
                      <td className="px-3 py-2 font-bold text-navy">#{o.numero}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(o.intervention_date)}</td>
                      <td className="px-3 py-2 font-medium text-navy">{o.technician_name || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{o.ticket_code || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="space-y-1">
                          {(o.items || []).map((it, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Boxes size={13} className="text-navy" />
                              <span>{it.name}{it.sku ? ` · ${it.sku}` : ""}</span>
                              {it.supplier && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{it.supplier}</span>}
                              <span className="font-bold">×{it.qty}</span>
                              {it.substitute_for && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700" title={`Richiesto: ${it.substitute_for.name}`}>↔ in sost. di {it.substitute_for.name}</span>}
                            </div>
                          ))}
                        </div>
                        {o.sync_error && <p className="mt-1 text-xs text-rose-600">Sync: {o.sync_error}</p>}
                      </td>
                      <td className="px-3 py-2"><Pill s={o.status} /></td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {o.status !== "evaso" && (o.sync_error || !o.synced) && (
                            <Button size="icon" variant="ghost" title="Reinvia al Magazzino" onClick={() => resend(o.id)} data-testid={`odt-resend-${o.numero}`}><Send size={15} /></Button>
                          )}
                          {o.status === "evaso" ? (
                            <span className="text-xs text-muted-foreground">Storico</span>
                          ) : (
                            <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" title="Elimina ODT" onClick={() => removeOdt(o)} data-testid={`odt-delete-${o.numero}`}><Trash2 size={15} /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            </>
          )}
        </TabsContent>

        {/* -------------------- MATERIALI MANCANTI -------------------- */}
        <TabsContent value="mancanti" className="mt-4">
          {missing.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-14 text-center"><PackageX className="h-9 w-9 text-muted-foreground/40" /><p className="font-semibold">Nessun materiale mancante</p></Card>
          ) : (
            <Card className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="missing-table">
                <thead className="bg-secondary/60 text-left text-xs font-bold uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">N°</th>
                    <th className="px-3 py-2">Intervento</th>
                    <th className="px-3 py-2">Ticket</th>
                    <th className="px-3 py-2">Tecnico</th>
                    <th className="px-3 py-2">Materiale</th>
                    <th className="px-3 py-2">Fornitore</th>
                    <th className="px-3 py-2 text-right">Q.tà</th>
                    <th className="px-3 py-2">Stato</th>
                    <th className="px-3 py-2 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((m) => (
                    <tr key={m.id} className={`border-t border-border/60 ${m.status === "gestito" ? "opacity-60" : ""}`} data-testid={`missing-row-${m.numero}`}>
                      <td className="px-3 py-2 font-bold text-navy">#{m.numero}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(m.intervention_date)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{m.ticket_code || "—"}</td>
                      <td className="px-3 py-2">{m.technician_name || "—"}</td>
                      <td className="px-3 py-2 font-medium text-navy">{m.name}{m.sku ? ` · ${m.sku}` : ""}</td>
                      <td className="px-3 py-2 text-muted-foreground">{m.supplier || "—"}</td>
                      <td className="px-3 py-2 text-right font-bold">{m.qty}</td>
                      <td className="px-3 py-2">
                        {m.status === "gestito"
                          ? <span className="inline-block rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">Gestito</span>
                          : <span className="inline-block rounded-full border border-rose-200 bg-rose-100 px-2.5 py-0.5 text-xs font-bold text-rose-700">Da ordinare</span>}
                        {m.status === "gestito" && m.handled_by && <span className="ml-2 text-xs text-muted-foreground">da {m.handled_by}</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end">
                          {m.status === "da_ordinare"
                            ? <Button size="sm" variant="outline" onClick={() => handleMissing(m)} data-testid={`missing-handle-${m.numero}`}><Check size={14} /> Gestito</Button>
                            : <Button size="icon" variant="ghost" title="Riapri" onClick={() => reopenMissing(m)} data-testid={`missing-reopen-${m.numero}`}><RotateCcw size={14} /></Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* -------------------- DETTAGLIO ODT -------------------- */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="odt-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-navy">
                  <Truck size={18} /> ODT #{selected.numero} <Pill s={selected.status} />
                </DialogTitle>
                <DialogDescription>Dettaglio dei materiali inviati al Magazzino per questo ordine di trasferimento.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2"><Hash size={14} className="text-muted-foreground" /> <span className="text-muted-foreground">Ticket:</span> <span className="font-semibold text-navy">{selected.ticket_code || "—"}</span></div>
                  <div className="flex items-center gap-2"><Calendar size={14} className="text-muted-foreground" /> <span className="text-muted-foreground">Intervento:</span> <span className="font-semibold">{fmtDate(selected.intervention_date)}</span></div>
                  <div className="flex items-center gap-2"><User size={14} className="text-muted-foreground" /> <span className="text-muted-foreground">Tecnico:</span> <span className="font-semibold">{selected.technician_name || "—"}</span></div>
                  {selected.magazzino_order_id && <div className="flex items-center gap-2"><FileText size={14} className="text-muted-foreground" /> <span className="text-muted-foreground">Rif. Magazzino:</span> <span className="font-mono text-xs">{selected.magazzino_order_id}</span></div>}
                </div>

                <div>
                  <p className="mb-2 flex items-center gap-2 text-sm font-bold text-navy"><Boxes size={15} /> Materiali inviati al Magazzino ({(selected.items || []).length})</p>
                  <div className="overflow-hidden rounded-md border border-border">
                    <table className="w-full text-sm" data-testid="odt-detail-items">
                      <thead className="bg-secondary/60 text-left text-xs font-bold uppercase text-muted-foreground">
                        <tr><th className="px-3 py-2">Materiale</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Fornitore</th><th className="px-3 py-2 text-right">Q.tà</th>{selected.status === "evaso" && <th className="px-3 py-2">Seriali</th>}</tr>
                      </thead>
                      <tbody>
                        {(selected.items || []).length === 0 && <tr><td colSpan={5} className="px-3 py-3 text-center text-muted-foreground">Nessun materiale</td></tr>}
                        {(selected.items || []).map((it, i) => {
                          const serials = (selected.serials_assigned || []).find((s) => s.product_id === it.product_id)?.serial_numbers || [];
                          return (
                            <tr key={i} className="border-t border-border/60">
                              <td className="px-3 py-2 font-medium text-navy">{it.name}{it.substitute_for && <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">↔ in sostituzione di {it.substitute_for.name}</span>}</td>
                              <td className="px-3 py-2 text-muted-foreground">{it.sku || "—"}</td>
                              <td className="px-3 py-2 text-muted-foreground">{it.supplier || "—"}</td>
                              <td className="px-3 py-2 text-right font-bold">×{it.qty}</td>
                              {selected.status === "evaso" && <td className="px-3 py-2 text-xs">{serials.length ? serials.join(", ") : "—"}</td>}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {(selected.missing || []).length > 0 && (
                  <div>
                    <p className="mb-2 flex items-center gap-2 text-sm font-bold text-rose-700"><PackageX size={15} /> Non disponibili al Centrale ({selected.missing.length})</p>
                    <div className="space-y-1">
                      {selected.missing.map((m, i) => (
                        <div key={i} className="flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm text-rose-700">
                          <span>{m.name}{m.sku ? ` · ${m.sku}` : ""}{m.supplier ? <span className="ml-1.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-rose-600">{m.supplier}</span> : null}</span><span className="font-bold">×{m.qty}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.sync_error && <p className="text-xs text-rose-600">Errore sync Magazzino: {selected.sync_error}</p>}
                <p className="text-xs text-muted-foreground">Creato: {selected.created_at ? new Date(selected.created_at).toLocaleString("it-IT") : "—"}{selected.evaso_at ? ` · Evaso: ${new Date(selected.evaso_at).toLocaleString("it-IT")}` : ""}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
